import React, { useState } from 'react';
import type { TabsProps } from 'antd';
import { Badge, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import ConventionSignComponent from './ConventionSignComponent';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import { Roles } from '../Types/interfaces';
import { MenuGroups } from '../Constants/menuKeys';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useUserOrganizations } from '../hooks/useOrganizations';
import { useInternshipProcessesByRole } from '../hooks/useInternshipProcesses';
import { useReadyToSignConventionsOfProjectCoordinator } from '../hooks/BadgeHooks/ProjectCoordinatorHooks';

// stable reference so the badge memo doesn't recompute on every render
const EMPTY_ARRAY: InternshipProcessWithNames[] = [];

export const UniversityProjectCoordinatorComponent = (): React.ReactElement => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const loggedInUser = useCurrentUser();
  const [activeKey, setActiveKey] = useState<string>('conventions');

  // Same arguments as inside ConventionSignComponent, so react-query
  // shares the cached result instead of firing a second request.
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const userOrganizationIds = userOrganizations?.map((org) => org.id) ?? [];
  const { data: internshipProcesses = EMPTY_ARRAY } =
    useInternshipProcessesByRole(
      loggedInUser.id,
      Roles.UniversityProjectCoordinator,
      userOrganizationIds
    );

  const readyToSignConventions =
    useReadyToSignConventionsOfProjectCoordinator(internshipProcesses);

  const refreshCounts = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ROLE,
    });
  };

  // Add new coordinator features as extra entries here.
  const items: TabsProps['items'] = [
    {
      key: 'conventions',
      label: (
        <span>
          {t('projectCoordinatorComp.conventions')}{' '}
          <Badge
            count={readyToSignConventions.length}
            overflowCount={10}
            size="small"
          />
        </span>
      ),
      children: (
        <ConventionSignComponent
          menuGroup={MenuGroups.UniversityProjectCoordinator}
          role={Roles.UniversityProjectCoordinator}
          recount={refreshCounts}
        />
      ),
    },
  ];

  return (
    <Tabs
      centered
      type="card"
      activeKey={activeKey}
      onTabClick={(key) => setActiveKey(key)}
      style={{ paddingTop: 10, marginBottom: 100 }}
      items={items}
    />
  );
};
